import React , {useState, useEffect} from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import ForgetPassword from '@/components/ForgetPassword';
import { toast } from '@/components/ui/sonner';
import SignUp from '@/components/SignUp';
import BusyIndicator from '@/components/BusyIndicator';
import { GoogleLogin } from '@react-oauth/google';
import { authService } from '@/services/authService';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

export default function Login() {
    const{ login, userDetails, isInitialized } = useApp();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgetPassword, setForgetPassword] = useState(false);
    const [signUp,setSignUp] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    // Safety net: if an already-authenticated user lands on /login (e.g. after the
    // cold-start hydration completes), bounce them to the dashboard.
    useEffect(() => {
        if (isInitialized && userDetails) {
            navigate('/', { replace: true });
        }
    }, [isInitialized, userDetails, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!email || !password){
            toast.error('\nאנא מלא את כל השדות\n', {duration: 4000,position: 'top-center',style: {direction: 'rtl',fontSize: '24px'}});
            return;
        }
        setIsBusy(true);
        const loginResult : Boolean = await login({ email: email ,password: password});
        if(!loginResult){
            toast.error('\nאימייל או סיסמה שגויים\n', {duration: 4000,position: 'top-center',style: {direction: 'rtl',fontSize: '24px'}});
            setIsBusy(false);
            return;
        }
        navigate('/', { replace: true });
    }    

    const processGoogleIdToken = async (idToken: string) => {
        setIsBusy(true);
        try {
            const { data } = await authService.googleLogin(idToken);
            localStorage.setItem('access_token', data.session.access_token);
            localStorage.setItem('refresh_token', data.session.refresh_token);
            await login(data);
            navigate('/', { replace: true });
        } catch (error) {
            toast.error('\nשגיאה בהתחברות עם Google\n', {duration: 4000,position: 'top-center',style: {direction: 'rtl',fontSize: '24px'}});
        } finally {
            setIsBusy(false);
        }
    };

    const handleGoogleSuccess = (credentialResponse) =>
        processGoogleIdToken(credentialResponse.credential);

    const handleNativeGoogle = async () => {
        try {
            const result = await SocialLogin.login({
                provider: 'google',
                options: { scopes: ['email', 'profile'] },
            });
            const idToken = (result as any)?.result?.idToken;
            if (!idToken) throw new Error('No idToken returned');
            await processGoogleIdToken(idToken);
        } catch (error) {
            toast.error('\nשגיאה בהתחברות עם Google\n', {duration: 4000,position: 'top-center',style: {direction: 'rtl',fontSize: '24px'}});
        }
    };

    const backToLogin = () => {
        setForgetPassword(false);
    }
    const backToLoginSignup = () => {
        setSignUp(false);
    }

    if(forgetPassword){
        return(<ForgetPassword onBackToLogin={backToLogin} />);
    }
    if(signUp){
        return(<SignUp onBackToLogin={backToLoginSignup} />);
    }
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold mb-6 text-center">התחברות למערכת</h2>
                <form className="space-y-4">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">אימייל</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                            placeholder="הכנס אימייל"
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">סיסמה</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                            placeholder="הכנס סיסמה"
                        />
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors"
                        onClick={handleSubmit}
                    >
                        התחברות
                    </button>
                    <div className="mt-4 w-full rounded-lg" >
                        {Capacitor.isNativePlatform() ? (
                            <button
                                type="button"
                                onClick={handleNativeGoogle}
                                className="w-full border border-gray-300 bg-white text-gray-700 py-2 px-4 rounded-full hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                            >
                                המשך עם Google
                            </button>
                        ) : (
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => {
                                    toast.error('\nשגיאה בהתחברות עם Google\n', {duration: 4000,position: 'top-center',style: {direction: 'rtl',fontSize: '24px'}});
                                }}
                                text="continue_with"
                                shape="circle"
                            />
                        )}
                    </div>
                    <div className="text-sm text-center text-gray-600 mt-4">
                        אין לך חשבון? <a className="text-primary hover:underline" onClick={(e)=> setSignUp(true)}>הרשמה</a>
                    </div>
                    <div className="text-xs text-center text-gray-400 mt-2">
                        שכחת סיסמה? <a className="text-primary hover:underline" onClick={(e)=> setForgetPassword(true)}>אפס סיסמה</a>
                    </div>
                </form>
            </div>
            {isBusy && <BusyIndicator />}
        </div>
    );
}